/**
 * Portada de cada presupuesto en el puerto: el color de las velas y del
 * casco del galeón, o una foto que se pinta en las velas. Se guarda en este
 * dispositivo (localStorage), por presupuesto.
 */

export type PortadaPredefinida = {
  id: string;
  vela: string;
  casco: string;
  bandera: string;
};

export type Portada =
  | { tipo: 'predefinida'; id: string }
  | { tipo: 'foto'; datos: string };

// Los tres primeros son los de la maqueta que eligió Vadik.
export const PORTADAS: readonly PortadaPredefinida[] = [
  { id: 'menta', vela: '#dff3e8', casco: '#8fd3b0', bandera: '#2e8b62' },
  { id: 'arena', vela: '#f3d9a4', casco: '#c79a6a', bandera: '#e0766b' },
  { id: 'cielo', vela: '#bcd4f5', casco: '#7fa2d6', bandera: '#8a63d2' },
  { id: 'coral', vela: '#f6c6bb', casco: '#d98273', bandera: '#2e8b62' },
  { id: 'lavanda', vela: '#e1d6f7', casco: '#9c86d6', bandera: '#d08a2e' },
  { id: 'noche', vela: '#e8eef7', casco: '#5d6f96', bandera: '#8fd3b0' },
];

const PREFIJO = 'rumbo-portada:';

/** Cada presupuesto nace con un color distinto, siempre el mismo. */
export function portadaPorDefecto(clave: string): PortadaPredefinida {
  let h = 0;
  for (let i = 0; i < clave.length; i++) {
    h = (h * 31 + clave.charCodeAt(i)) >>> 0;
  }
  return PORTADAS[h % PORTADAS.length];
}

export function buscarPredefinida(id: string): PortadaPredefinida | null {
  return PORTADAS.find(p => p.id === id) ?? null;
}

/** Lee la portada guardada; si no hay o está rota, null. */
export function interpretarPortada(texto: string | null): Portada | null {
  if (!texto) {
    return null;
  }
  try {
    const valor = JSON.parse(texto);
    if (valor?.tipo === 'predefinida' && buscarPredefinida(valor.id)) {
      return { tipo: 'predefinida', id: valor.id };
    }
    if (
      valor?.tipo === 'foto' &&
      typeof valor.datos === 'string' &&
      valor.datos.startsWith('data:image/')
    ) {
      return { tipo: 'foto', datos: valor.datos };
    }
  } catch {
    // Portada rota: se usa la de por defecto.
  }
  return null;
}

export function leerPortada(clave: string): Portada | null {
  try {
    return interpretarPortada(window.localStorage.getItem(PREFIJO + clave));
  } catch {
    return null;
  }
}

/** Devuelve false si no se pudo guardar (sin espacio o modo privado). */
export function guardarPortada(clave: string, portada: Portada): boolean {
  try {
    window.localStorage.setItem(PREFIJO + clave, JSON.stringify(portada));
    return true;
  } catch {
    return false;
  }
}

export type ColoresBarco = {
  vela: string;
  casco: string;
  bandera: string;
  foto: string | null;
};

/** Colores del galeón para una portada (la foto lleva casco neutro). */
export function coloresBarco(
  clave: string,
  portada: Portada | null,
): ColoresBarco {
  if (portada?.tipo === 'foto') {
    return {
      vela: '#e8eef7',
      casco: '#8fd3b0',
      bandera: '#2e8b62',
      foto: portada.datos,
    };
  }
  const elegida =
    (portada?.tipo === 'predefinida' && buscarPredefinida(portada.id)) ||
    portadaPorDefecto(clave);
  return {
    vela: elegida.vela,
    casco: elegida.casco,
    bandera: elegida.bandera,
    foto: null,
  };
}

/** Reduce una foto a un cuadrado pequeño (JPEG) para guardarla. */
export async function reducirFoto(archivo: Blob, lado = 240): Promise<string> {
  const url = URL.createObjectURL(archivo);
  try {
    const imagen = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
    const corte = Math.min(imagen.width, imagen.height);
    const lienzo = document.createElement('canvas');
    lienzo.width = lado;
    lienzo.height = lado;
    const ctx = lienzo.getContext('2d');
    if (!ctx) {
      throw new Error('Sin canvas');
    }
    ctx.drawImage(
      imagen,
      (imagen.width - corte) / 2,
      (imagen.height - corte) / 2,
      corte,
      corte,
      0,
      0,
      lado,
      lado,
    );
    return lienzo.toDataURL('image/jpeg', 0.8);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * La portada va con la clave del presupuesto, que cambia al subirlo a la nube
 * (pasa a ser su id en la nube) o al dejarlo solo en el dispositivo. Esto la
 * copia a la clave nueva para que el barco no cambie de color.
 */
export function trasladarPortada(desde: string, hasta: string) {
  if (desde === hasta) {
    return;
  }
  const portada = leerPortada(desde);
  if (portada) {
    guardarPortada(hasta, portada);
  }
}
